import { LoanProductCategory } from '@mediaingenuity/totallymoney.types'
import { GetServerSideProps } from 'next'
import Head from 'next/head'

import Box from '@ilg-totallymoney/ui/components/Box'
import LinkButton from '@ilg-totallymoney/ui/components/LinkButton'

import MetadataHead from '@components/MetadataHead'
import NotFoundPage from '@components/NotFoundPage'

import { CategoryPath } from '@config/categories'

import useAppQueryState from '@hooks/useAppQueryState'

import UnscoredTableRoute from '@routes/UnscoredTableRoute/UnscoredTableRoute'

import { getCategoryIdForPath } from '@utils/getCategoryIdForPath'
import { getIsValidCategoryPath } from '@utils/getIsValidCategoryPath'
import { getQueryCategoryLoanFilterValues } from '@utils/getQueryCategoryLoanFilterValues'
import { getUnscoredCategoryMetadata } from '@utils/getUnscoredCategoryMetadata'

export interface UnscoredCategoryPageProps {
    category: CategoryPath
    loanAmount?: number
    loanTerm?: number
}

const UnscoredCategoryPage = ({
    category,
    loanAmount,
    loanTerm,
}: UnscoredCategoryPageProps) => {
    const { appQueryState, getRouteUrl } = useAppQueryState()

    if (!getIsValidCategoryPath(category)) {
        return (
            <>
                <Head>
                    <title>Page not found</title>
                </Head>
                <NotFoundPage>
                    <Box pt="space30" maxWidth={['100%', '280px']}>
                        <LinkButton
                            variant="primary"
                            text="Back to loans"
                            href={getRouteUrl('')}
                        />
                    </Box>
                </NotFoundPage>
            </>
        )
    }

    return (
        <>
            <MetadataHead
                metadata={getUnscoredCategoryMetadata(
                    appQueryState.categoryId ??
                        (category as LoanProductCategory)
                )}
                canIndex={true}
            />
            <UnscoredTableRoute
                initialQueryState={{ loanAmount, loanTerm, category }}
            />
        </>
    )
}

export const getServerSideProps: GetServerSideProps<
    UnscoredCategoryPageProps
> = async ({ query }) => {
    const categoryId = getCategoryIdForPath(query.category as CategoryPath)
    const { loanAmount, loanTerm } = getQueryCategoryLoanFilterValues({
        category: categoryId,
        loanAmount: query.loanAmount as string,
        loanTerm: query.loanTerm as string,
    })

    return {
        props: {
            category: query.category as CategoryPath,
            loanAmount,
            loanTerm,
        },
    }
}

export default UnscoredCategoryPage
